import { FlickeringGrid } from "@/components";
import { ChatInput } from "@/components/Chat/input/ChatInput";
import { Button } from "@/components/ui/button";
import { Highlight } from "@/components/ui/aceternity-ui/hero-highlight";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { TempChatProvider } from "@/contexts/TempChatContext";
import { useMobileOptimized } from "@/hooks/useMobileOptimized";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Optimized CSS animations to replace Framer Motion
const heroAnimations = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.6s ease-out forwards;
  }

  .animate-fade-in {
    animation: fadeIn 0.5s ease-out forwards;
  }

  .animate-delay-100 {
    animation-delay: 0.1s;
  }

  .animate-delay-200 {
    animation-delay: 0.2s;
  }

  .animate-delay-300 {
    animation-delay: 0.3s;
  }

  .animate-delay-400 {
    animation-delay: 0.4s;
  }

  .animate-delay-500 {
    animation-delay: 0.5s;
  }

  .animate-delay-600 {
    animation-delay: 0.6s;
  }

  .initial-hidden {
    opacity: 0;
    transform: translateY(20px);
  }
`;

function HeroSectionContent({ onShowAuthOverlay }) {
  const navigate = useNavigate();

  const handleSendMessage = useCallback(
    ({ content }) => {
      if (!content?.trim()) return;

      // Navigate to temp chat page with the message
      navigate("/temp-chat", {
        state: {
          initialMessage: content.trim(),
          fromLanding: true,
        },
      });
    },
    [navigate]
  );

  const { isMobile, shouldReduceMotion } = useMobileOptimized();

  // Add hysteresis to prevent FlickeringGrid mount/unmount thrashing during resize
  const [stableMobile, setStableMobile] = useState(isMobile);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setStableMobile(isMobile);
    }, 300);
    return () => clearTimeout(timeout);
  }, [isMobile]);

  return (
    <section className="relative flex flex-col items-center justify-start pt-16 pb-32 overflow-hidden">
      {/* Inject optimized animations */}
      <style dangerouslySetInnerHTML={{ __html: heroAnimations }} />

      {/* Enhanced Background Gradient - Lower z-index */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-purple-950/20 dark:via-purple-950/20 via-purple-200/30 to-background pointer-events-none z-[1]" />

      {/* Interactive Background - Only on desktop with reduced complexity */}
      {/* Uses stableMobile to prevent mount/unmount thrashing during resize */}
      {!shouldReduceMotion && !stableMobile && (
        <div className="absolute inset-0 overflow-hidden">
          <FlickeringGrid
            className="relative inset-0 z-0 [mask-image:radial-gradient(600px_circle_at_center,white,transparent)]"
            squareSize={8}
            gridGap={10}
            color="#a855f7"
            maxOpacity={0.2}
            flickerChance={0.02}
          />
        </div>
      )}

      {/* Hero Content */}
      <div className="container relative z-10 px-4 sm:px-6 mx-auto pt-8">
        <div className="flex flex-col items-center justify-center space-y-4">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-neutral-700 dark:text-white text-center mx-auto initial-hidden animate-fade-in-up">
            Meet Artifact
          </h1>

          <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mx-auto initial-hidden animate-fade-in-up animate-delay-100">
            the all-in-one AI assistant
          </div>

          <div className="mt-6 max-w-3xl mx-auto initial-hidden animate-fade-in-up animate-delay-200">
            <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-300 text-center">
              Collaborate, create, and chat with the world&apos;s best AI tools
              —{" "}
              <Highlight className="text-black dark:text-white">
                all in one place
              </Highlight>
              .
            </p>
          </div>
        </div>

        {/* Landing Page Chat Input - Same component as temp-chat */}
        <div className="w-full max-w-3xl mx-auto px-4 sm:px-0 pt-8 initial-hidden animate-fade-in-up animate-delay-300">
          <SidebarProvider>
            <ChatInput
              onSendMessage={handleSendMessage}
              placeholder="Type a message to try out our AI assistant..."
              isTempChat={true}
              disableTools={true}
              disableFileUpload={true}
              isLimitedMode={true}
              disableDeepResearch={true}
              maxFiles={0}
              onShowAuthOverlay={onShowAuthOverlay}
            />
          </SidebarProvider>

          {/* Try Now Button */}
          <div className="flex justify-center mt-12">
            <Button
              onClick={() => navigate("/auth/signup")}
              className="bg-purple-600 text-white hover:bg-purple-700 px-8 py-5 text-base font-medium rounded-full transition-all duration-300"
            >
              Try Now
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HeroSection({ onShowAuthOverlay }) {
  return (
    <TempChatProvider>
      <HeroSectionContent onShowAuthOverlay={onShowAuthOverlay} />
    </TempChatProvider>
  );
}

export default HeroSection;
